import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { ArrowUpRight, Download, FileText, Sparkles, Target } from "lucide-react";

import {
  downloadAttemptResponses,
  downloadAttemptPdf,
  formatDuration,
  formatTimestamp,
  getAttemptById,
  getAttemptReport,
  getLatestCompletedAttempt,
  getProfile,
} from "../lib/profileEngine";
import { DEFAULT_RETURN_URL, getReturnUrl } from "../lib/returnUrl";
import { useCurrentTheme } from "../lib/theme";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const shortLabel = (label) =>
  label
    .split(/[\s/()-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 4);

const truncateText = (text, maxLength = 180) => {
  if (!text || text.length <= maxLength) return text;
  const cut = text.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : maxLength)}…`;
};

const varianceStyle = (variance) => {
  const magnitude = Math.abs(variance);
  if (magnitude >= 20) {
    return {
      panel: "border-amber-400/35 bg-[rgba(235,146,52,0.10)]",
      chip: "bg-amber-300/16 text-[color:var(--warn-text)] border-amber-300/24",
      pulse: true,
    };
  }
  if (magnitude >= 10) {
    return {
      panel: "border-[#8ea2c4]/30 bg-[#8ea2c4]/8",
      chip: "bg-[#8ea2c4]/12 text-[color:var(--info-text)] border-[#8ea2c4]/24",
      pulse: false,
    };
  }
  return {
    panel: "border-emerald-300/18 bg-emerald-300/6",
    chip: "bg-emerald-300/10 text-[color:var(--success-text)] border-emerald-300/20",
    pulse: false,
  };
};

const RingMeter = ({ label, value, tone, members, index }) => {
  const size = 156;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;

  return (
    <motion.article
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="glass rounded-sm p-5 md:p-6"
    >
      <div className="flex items-center justify-center">
        <div className="relative inline-flex items-center justify-center">
          <svg width={size} height={size} className="-rotate-90">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
            <motion.circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={tone}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={c}
              initial={{ strokeDashoffset: c }}
              animate={{ strokeDashoffset: offset }}
              transition={{ duration: 1.4, delay: 0.2 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-mono text-[16px] tracking-widest-2 text-ink">INDEX</div>
            <div className="font-mono text-4xl text-ink tabular leading-none mt-1">
              {value}
              <span className="text-xl text-ink-2 ml-1">%</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-5">
        <div className="font-serif text-[1.9rem] leading-none text-ink">{label}</div>
        <div className="mt-3 text-base text-ink-2 leading-relaxed">{members.join(" · ")}</div>
      </div>
    </motion.article>
  );
};

const RadarTooltip = ({ active, payload, primaryShort, secondaryShort }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div className="glass-strong border border-line-2 rounded-sm px-4 py-3 shadow-[0_20px_60px_rgba(0,0,0,0.32)]">
      <div className="font-serif text-lg text-ink">{point.factor}</div>
      <div className="mt-2 space-y-1 text-[16px] font-mono tracking-widest-2 text-ink-2">
        <div>{primaryShort} SCORE · {point.primary}</div>
        <div>{secondaryShort} SCORE · {point.secondary}</div>
        <div>VARIANCE · {point.variance}</div>
      </div>
    </div>
  );
};

const Report = () => {
  const { id: profileId } = useParams();
  const hideScores = profileId === "ssp";
  const [searchParams] = useSearchParams();
  const profile = getProfile();
  const attemptId = searchParams.get("attempt");
  const theme = useCurrentTheme();
  const isLight = theme === "light";
  const chartGridStroke = isLight ? "rgba(31,61,43,0.16)" : "rgba(255,255,255,0.08)";
  const chartAxisLabelColor = isLight ? "#2f3d2f" : "#cfc9bc";
  const chartPrimaryStroke = isLight ? "#2f5940" : "#B5C69B";
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedFactorId, setSelectedFactorId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingResponses, setDownloadingResponses] = useState(false);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const nextAttempt = attemptId
          ? await getAttemptById(profileId, attemptId)
          : await getLatestCompletedAttempt(profileId);
        if (!nextAttempt) {
          throw new Error("No completed profile was found.");
        }
        nextAttempt.report = await getAttemptReport(profileId, nextAttempt.id);
        if (!ignore) {
          setAttempt(nextAttempt);
          setSelectedFactorId(nextAttempt.report?.factors?.[0]?.id || null);
        }
      } catch (err) {
        if (!ignore) {
          setError(err.message || "Unable to load report.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, [attemptId, profileId]);

  const syncPending = attempt?.redacamySync === "pending";
  const attemptIdForSync = attempt?.id;
  useEffect(() => {
    if (!syncPending) return undefined;
    const timer = window.setInterval(async () => {
      try {
        const latest = await getAttemptById(profileId, attemptIdForSync);
        if (latest.redacamySync !== "pending") {
          setAttempt((previous) => (previous ? { ...previous, redacamySync: latest.redacamySync } : previous));
        }
      } catch {
        // keep polling; the user is never blocked by the sync
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [syncPending, profileId, attemptIdForSync]);
  const returnUrl = getReturnUrl(profileId) || DEFAULT_RETURN_URL;

  const report = attempt?.report;
  const factorData = useMemo(
    () =>
      (report?.factors || []).map((factor) => ({
        id: factor.id,
        factor: factor.name,
        primary: factor.primaryScore,
        secondary: factor.secondaryScore,
        variance: factor.varianceScore,
      })),
    [report],
  );
  const activeFactor =
    report?.factors?.find((factor) => factor.id === selectedFactorId) || report?.factors?.[0] || null;
  const overall = report?.secondaryDimension?.average || 0;

  const handleDownload = async () => {
    if (!attempt) return;
    try {
      setDownloading(true);
      await downloadAttemptPdf(profileId, attempt.id);
    } catch (err) {
      setError(err.message || "Unable to download PDF report.");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadResponses = async () => {
    if (!attempt) return;
    try {
      setDownloadingResponses(true);
      await downloadAttemptResponses(profileId, attempt.id);
    } catch (err) {
      setError(err.message || "Unable to download questions and answers.");
    } finally {
      setDownloadingResponses(false);
    }
  };

  if (loading) return <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex items-center justify-center text-ink-2">Loading report...</main>;
  if (!attempt || !report) return <main className="relative min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex items-center justify-center text-[color:var(--warn-text)]">{error || "Report unavailable."}</main>;

  return (
    <main className="relative pt-8 md:pt-10 lg:pt-24 pb-14 md:pb-16 px-4 md:px-10 lg:px-16 lg:pl-32 xl:pl-36" data-testid="report-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.85 }}
        className="grid grid-cols-12 gap-6 md:gap-8 items-end mb-8 md:mb-10"
      >
        <div className="col-span-12 lg:col-span-8">
          <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-3">
            ◇ {report.profileName.toUpperCase()} · {profile.fullName || "PARTICIPANT"}
          </div>
          <h1 className="font-serif text-4xl md:text-6xl xl:text-7xl tracking-tighter leading-[0.95] text-ink">
            Interactive report.
            <br />
            <span className="italic text-gold gold-glow">Visualize the factor pattern.</span>
          </h1>
          <div className="font-mono text-[16px] tracking-widest-2 text-ink mt-6">
            COMPLETED · {formatTimestamp(attempt.completedAt)} · ACTIVE TIME · {formatDuration(attempt.analytics.totalActiveMs)}
          </div>
          {error ? <div className="mt-4 text-base text-[color:var(--warn-text)]">{error}</div> : null}
          {syncPending ? (
            <div className="mt-4 text-base text-ink-2" data-testid="redacamy-sync">
              Your report is ready — syncing with Redacamy…
            </div>
          ) : null}
        </div>
        <div className="col-span-12 lg:col-span-4 flex flex-col items-start lg:items-end gap-3">
          {!hideScores ? (
            <motion.div
              animate={{ scale: [1, 1.015, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="glass rounded-sm px-6 py-5"
            >
              <div className="font-mono text-[16px] tracking-widest-2 text-ink">
                {report.secondaryDimension.label.toUpperCase()} INDEX
              </div>
              <div className="font-mono text-5xl text-ink tabular leading-none mt-2">
                {overall}
                <span className="text-2xl text-ink-2 ml-1">%</span>
              </div>
            </motion.div>
          ) : null}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full lg:w-auto">
            <a href={returnUrl} className="btn-ghost w-full sm:w-auto justify-center" data-testid="return-to-redacamy">
              <ArrowUpRight className="w-4 h-4" /> Return to Redacamy
            </a>
            <button onClick={handleDownload} className="btn-gold w-full sm:w-auto" disabled={downloading}>
              <Download className="w-4 h-4" /> {downloading ? "Preparing PDF..." : "Download PDF"}
            </button>
            <button
              onClick={handleDownloadResponses}
              className="btn-ghost w-full sm:w-auto justify-center"
              disabled={downloadingResponses}
            >
              <FileText className="w-4 h-4" />{" "}
              {downloadingResponses ? "Preparing responses..." : "Download Questions & Answers"}
            </button>
          </div>
        </div>
      </motion.div>

      {!hideScores ? (
      <>
      <section className="grid grid-cols-12 gap-5 md:gap-6 mb-8 md:mb-10">
        <motion.article
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="col-span-12 glass rounded-sm p-5 md:p-8"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
            <div>
              <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ GEOMETRIC RADAR</div>
              <h3 className="font-serif text-[2rem] md:text-[2.5rem] text-ink leading-none mt-2">
                {report.primaryDimension.label} versus {report.secondaryDimension.label}
              </h3>
            </div>
            <div className="text-base text-ink-2 max-w-md leading-relaxed">
              The outer field shows {report.primaryDimension.label.toLowerCase()}, while the inner field shows {report.secondaryDimension.label.toLowerCase()} across every scored factor.
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5 items-center">
            <div className="col-span-12 lg:col-span-8 h-[440px] md:h-[520px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart outerRadius="72%" data={factorData}>
                  <defs>
                    <radialGradient id="secondaryField" cx="50%" cy="50%" r="60%">
                      <stop offset="0%" stopColor="rgba(142,162,196,0.45)" />
                      <stop offset="100%" stopColor="rgba(142,162,196,0.08)" />
                    </radialGradient>
                    <radialGradient id="primaryField" cx="50%" cy="50%" r="65%">
                      <stop offset="0%" stopColor="rgba(181,198,155,0.14)" />
                      <stop offset="100%" stopColor="rgba(181,198,155,0.02)" />
                    </radialGradient>
                  </defs>
                  <PolarGrid stroke={chartGridStroke} radialLines />
                  <PolarAngleAxis
                    dataKey="factor"
                    tick={({ payload, x, y, textAnchor, ...rest }) => (
                      <text
                        {...rest}
                        x={x}
                        y={y}
                        textAnchor={textAnchor}
                        fill={chartAxisLabelColor}
                        stroke="none"
                        fontSize="11"
                        fontFamily="JetBrains Mono"
                        letterSpacing="0.24em"
                      >
                        {shortLabel(payload.value)}
                      </text>
                    )}
                  />
                  <Tooltip
                    content={
                      <RadarTooltip
                        primaryShort={report.primaryDimension.shortLabel}
                        secondaryShort={report.secondaryDimension.shortLabel}
                      />
                    }
                  />
                  <Radar
                    name={report.primaryDimension.label}
                    dataKey="primary"
                    stroke={chartPrimaryStroke}
                    fill="url(#primaryField)"
                    fillOpacity={1}
                    strokeWidth={2}
                    strokeDasharray="5 4"
                  />
                  <Radar
                    name={report.secondaryDimension.label}
                    dataKey="secondary"
                    stroke="#8EA2C4"
                    fill="url(#secondaryField)"
                    fillOpacity={1}
                    strokeWidth={3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            <div className="col-span-12 lg:col-span-4 space-y-3">
              <div className="glass rounded-sm p-4">
                <div className="font-mono text-[16px] tracking-widest-2 text-ink">AXIS LEGEND</div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-base text-ink-2">
                  {factorData.map((item) => (
                    <div key={item.id} className="flex items-start gap-2 min-w-0">
                      <span className="font-mono text-[16px] text-gold w-10 shrink-0">{shortLabel(item.factor)}</span>
                      <span className="leading-tight break-words min-w-0">{item.factor}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass rounded-sm p-4">
                <div className="flex items-center gap-3 text-base text-ink-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#B5C69B]" />
                  {report.primaryDimension.label} polygon
                </div>
                <div className="flex items-center gap-3 text-base text-ink-2 mt-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-[#8EA2C4]" />
                  {report.secondaryDimension.label} polygon
                </div>
              </div>
            </div>
          </div>
        </motion.article>

        <motion.article
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="col-span-12 glass rounded-sm p-5 md:p-6"
        >
          <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ SUMMARY RINGS</div>
          <h3 className="font-serif text-[2rem] text-ink leading-none mt-2">Segmented readiness profile</h3>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(report.summaryGroups || []).map((item, index) => (
              <RingMeter
                key={item.label}
                label={item.label}
                value={item.value}
                members={item.members}
                index={index}
                tone={index === 0 ? "#98AB78" : index === 1 ? "#8EA2C4" : "#E6A85C"}
              />
            ))}
          </div>
        </motion.article>
      </section>

      <section className="grid grid-cols-12 gap-5 md:gap-6 mb-8 md:mb-10">
        <motion.article
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="col-span-12 xl:col-span-8 glass rounded-sm p-5 md:p-8"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-5">
            <div>
              <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ VARIANCE HEATMAP</div>
              <h3 className="font-serif text-[2rem] md:text-[2.4rem] text-ink leading-none mt-2">Actionable factor grid</h3>
            </div>
            <div className="text-base text-ink-2 max-w-md leading-relaxed">
              Larger variance means the participant&apos;s current lived behavior is farther from the desired or valued profile state in that factor.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {report.factors.map((factor, index) => {
              const tone = varianceStyle(factor.varianceScore);
              const active = selectedFactorId === factor.id;
              return (
                <motion.article
                  key={factor.id}
                  custom={index}
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                  className={`text-left rounded-sm border p-3.5 transition-all ${tone.panel} ${
                    active ? "shadow-[inset_0_0_0_1px_rgba(142,162,196,0.35)]" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 font-serif text-[1.05rem] leading-snug text-ink">{factor.name}</div>
                    <motion.div
                      animate={tone.pulse ? { scale: [1, 1.08, 1], opacity: [0.9, 1, 0.9] } : { scale: 1, opacity: 1 }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                      className={`shrink-0 whitespace-nowrap font-mono text-[12px] tracking-wide border rounded-full px-2 py-0.5 ${tone.chip}`}
                    >
                      V {factor.varianceScore}
                    </motion.div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 items-center gap-2 text-[12px] font-mono tracking-wide text-ink">
                    <span>{report.primaryDimension.shortLabel} {factor.primaryScore}</span>
                    <span className="text-center">{report.secondaryDimension.shortLabel} {factor.secondaryScore}</span>
                    <button
                      type="button"
                      onClick={() => setSelectedFactorId(factor.id)}
                      className={`justify-self-end inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] tracking-wide transition-colors cursor-pointer ${
                        active
                          ? "text-gold border-[color:var(--chip-active-border)] bg-[color:var(--chip-active-bg)]"
                          : "border-line text-ink-2 hover:border-line-2 hover:text-ink"
                      }`}
                    >
                      {active ? "OPEN" : "DETAILS"}
                    </button>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </motion.article>

        <motion.article
          custom={3}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="col-span-12 xl:col-span-4 glass rounded-sm p-5 md:p-6"
        >
          {activeFactor ? (
            <>
              <div className="font-mono text-[16px] tracking-widest-2 text-ink">◇ FACTOR DETAIL</div>
              <h3 className="font-serif text-[2rem] text-ink leading-none mt-2">{activeFactor.name}</h3>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: report.primaryDimension.shortLabel, value: activeFactor.primaryScore, tone: "text-gold" },
                  { label: report.secondaryDimension.shortLabel, value: activeFactor.secondaryScore, tone: "text-[color:var(--info-text)]" },
                  { label: "Variance", value: activeFactor.varianceScore, tone: "text-[color:var(--warn-text)]" },
                ].map((stat) => (
                  <div key={stat.label} className="min-w-0 overflow-hidden rounded-sm border border-line bg-white/[0.02] px-3 py-4">
                    <div className="font-mono text-[11px] tracking-wide text-ink truncate">{stat.label.toUpperCase()}</div>
                    <div className={`font-mono text-xl sm:text-2xl mt-2 ${stat.tone}`}>{stat.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-sm border border-line bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-gold font-mono text-[16px] tracking-widest-2">
                  <Target className="w-4 h-4" /> CURRENT READING
                </div>
                <p className="mt-3 text-base text-ink-2 leading-relaxed">
                  {truncateText(activeFactor.interpretation.secondary)}
                </p>
              </div>

              <div className="mt-4 rounded-sm border border-line bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 text-[color:var(--info-text)] font-mono text-[16px] tracking-widest-2">
                  <Sparkles className="w-4 h-4" /> ALIGNMENT NOTE
                </div>
                <p className="mt-3 text-base text-ink-2 leading-relaxed">
                  {truncateText(activeFactor.interpretation.variance)}
                </p>
              </div>

              <div className="mt-4 rounded-sm border border-[color:var(--chip-active-border)] bg-[color:var(--chip-active-bg)] p-4 text-center">
                <p className="text-base text-ink-2 leading-relaxed">
                  To read the complete interpretation for every factor, download the full PDF report.
                </p>
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloading}
                  className="btn-gold mt-3 mx-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" /> {downloading ? "Preparing PDF..." : "Download PDF"}
                </button>
              </div>
            </>
          ) : null}
        </motion.article>
      </section>
      </>
      ) : (
        <section className="glass rounded-sm p-6 md:p-10 mb-8 md:mb-10 text-center">
          <div className="font-mono text-[16px] tracking-widest-2 text-gold mb-3">◇ YOUR FULL REPORT</div>
          <h3 className="font-serif text-[1.8rem] md:text-[2.2rem] text-ink leading-tight max-w-2xl mx-auto">
            Your complete Spiritual Strengths interpretation is in your downloaded PDF report.
          </h3>
          <p className="text-ink-2 mt-4 max-w-2xl mx-auto leading-relaxed">
            It walks through each of your signature strengths, their shadows and compulsions, and a guided
            development path — download it above to read the full picture.
          </p>
        </section>
      )}

      <section className="glass-strong rounded-sm p-5 md:p-10 flex flex-wrap items-center justify-between gap-6">
        <div>
          <div className="font-serif text-2xl text-ink">Spiritual Profile Platform</div>
          <div className="text-base font-mono tracking-widest-2 text-ink mt-1">
            DATABASE-BACKED FACTOR VISUALS ACTIVE FOR RADAR, VARIANCE, AND SUMMARY STATES
          </div>
        </div>
        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
          <Link to="/dashboard" className="btn-gold w-full sm:w-auto">
            <FileText className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Report;
